import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { DuplicateReport } from './api';

interface Props {
  report: DuplicateReport;
  onOpen: (componentId: string) => void;
}

/**
 * Shown inside the form while it is still being filled in.
 *
 * It never blocks. The only hard rule in the system is the unique index on
 * (manufacturer, part number), because that case admits no judgement; every
 * other resemblance is a question for the person, who can see the shelf and the
 * datasheet and knows things the catalogue does not. So this offers two
 * buttons — open the existing part, or carry on — and no third one that decides
 * for them.
 */
export function DuplicateWarning({ report, onOpen }: Props) {
  const { t } = useTranslation();
  const [dismissed, setDismissed] = useState(false);

  if (dismissed || report.candidates.length === 0) return null;

  return (
    <section className="duplicates" aria-live="polite">
      <header className="duplicates__head">
        <strong>{t('components.duplicates.title')}</strong>
        <button type="button" onClick={() => setDismissed(true)}>
          {t('components.duplicates.dismiss')}
        </button>
      </header>

      <ul className="duplicates__list">
        {report.candidates.map(({ component, reason, score }) => (
          <li key={component.id}>
            <div className="duplicates__what">
              <button
                type="button"
                className="duplicates__open"
                onClick={() => onOpen(component.id)}
              >
                {component.name}
              </button>
              <span className="duplicates__code">{component.internal_code}</span>
              {component.mpn && <span className="duplicates__mpn">{component.mpn}</span>}
            </div>
            <div className="duplicates__why">
              {/* Saying why beats saying "possible duplicate": the reason is
                  what lets someone tell a real match from a coincidence. */}
              <span className={`duplicates__reason duplicates__reason--${reason}`}>
                {t(`components.duplicates.reason.${reason}`)}
              </span>
              {reason === 'similar_mpn' && (
                <span className="duplicates__score">{Math.round(score * 100)}%</span>
              )}
              <span className="duplicates__stock">
                {t('components.duplicates.onHand', { n: component.qty_on_hand })}
              </span>
            </div>
          </li>
        ))}
      </ul>

      {report.signature && (
        <details className="duplicates__signature">
          <summary>{t('components.duplicates.signature')}</summary>
          <code>{report.signature}</code>
        </details>
      )}
    </section>
  );
}
